import React, { useMemo, useState } from "react";
import { getRoleDisplay } from "@/lib/roleLabel";
import { Mail, ArrowRight, Building2, ArrowUp, ArrowDown, ArrowUpDown } from "lucide-react";
import { GenderIcon, GenderPicker } from "@/components/GenderControl";

const initialsOf = (u) => (u.name || u.email || "?").split(/[\s@]/).filter(Boolean).slice(0, 2).map((s) => s[0]?.toUpperCase()).join("");

const fmtDoj = (s) => new Date(`${s.slice(0, 10)}T00:00:00`).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
const tenure = (s) => {
    const d = new Date(`${s.slice(0, 10)}T00:00:00`), n = new Date();
    const m = Math.max(0, (n.getFullYear() - d.getFullYear()) * 12 + n.getMonth() - d.getMonth() - (n.getDate() < d.getDate() ? 1 : 0));
    return `${Math.floor(m / 12)}y ${m % 12}m`;
};

const FlatCells = ({ u, canEditGender, onSaved }) => (
    <>
        <td className="px-5 py-3" data-testid={`employee-gender-${u.user_id}`}><GenderPicker u={u} canEdit={canEditGender} onSaved={onSaved} /></td>
        <td className="px-5 py-3 text-sm text-slate-700" data-testid={`employee-designation-${u.user_id}`}>{u.designation || <span className="text-slate-300">—</span>}</td>
        <td className="px-5 py-3 text-sm text-slate-700 whitespace-nowrap" data-testid={`employee-doj-${u.user_id}`}>
            {u.date_of_joining ? <>{fmtDoj(u.date_of_joining)}<div className="text-[11px] text-amber-700 font-medium">{tenure(u.date_of_joining)}</div></> : <span className="text-slate-300">—</span>}
        </td>
    </>
);

export const EmployeeRow = ({ u, users, managerName, navigate, deptName, flat = false, canEditGender = false, onSaved }) => {
    const r = getRoleDisplay(u, users);
    return (
        <tr className="hover:bg-slate-50 transition-colors" data-testid={`employee-row-${u.user_id}`}>
            <td className="px-5 py-3">
                <div className="flex items-center gap-3">
                    <div className="h-9 w-9 rounded-full bg-teal-50 text-[#25908A] flex items-center justify-center font-heading font-semibold border border-teal-100 overflow-hidden flex-shrink-0">
                        {u.picture ? <img src={u.picture} alt="" className="h-full w-full object-cover" /> : initialsOf(u)}
                    </div>
                    <div className="min-w-0">
                        <div className="font-medium text-slate-900 truncate flex items-center gap-1.5">
                            <span className="truncate">{u.name}</span>
                            <GenderIcon gender={u.gender} testid={`employee-gender-icon-${u.user_id}`} />
                            {u._asAdditional && (
                                <span data-testid={`employee-additional-badge-${u.user_id}`} title="This user manages this department in addition to their home department" className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider bg-indigo-50 text-indigo-700 border border-indigo-200 flex-shrink-0">+manages</span>
                            )}
                        </div>
                        <div className="text-xs text-slate-500 flex items-center gap-1 truncate">
                            <Mail className="h-3 w-3 flex-shrink-0" />
                            <span className="truncate">{u.email}</span>
                        </div>
                        {!flat && u.designation && <div className="text-[11px] text-slate-400 truncate mt-0.5">{u.designation}</div>}
                        {deptName && <div className="text-[11px] text-[#25908A] truncate mt-0.5 flex items-center gap-1" data-testid={`employee-dept-${u.user_id}`}><Building2 className="h-3 w-3" />{deptName}</div>}
                    </div>
                </div>
            </td>
            {flat && <FlatCells u={u} canEditGender={canEditGender} onSaved={onSaved} />}
            <td className="px-5 py-3 hidden lg:table-cell text-slate-700 text-sm">{managerName(u.manager_id)}</td>
            <td className="px-5 py-3">
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${r.cls}`}>{r.label}</span>
            </td>
            <td className="px-5 py-3 text-right">
                <button onClick={() => navigate(`/employees/${u.user_id}`)} data-testid={`view-timeline-${u.user_id}`} className="inline-flex items-center gap-1 text-sm font-medium text-[#25908A] hover:text-[#2EB4AC]">
                    Timeline <ArrowRight className="h-4 w-4" />
                </button>
            </td>
        </tr>
    );
};

const SortBtn = ({ k, label, sort, setSort }) => (
    <button
        onClick={() => setSort((s) => ({ key: k, dir: s.key === k && s.dir === "asc" ? "desc" : "asc" }))}
        data-testid={`employees-sort-${k}`}
        className={`inline-flex items-center gap-1 uppercase tracking-wider hover:text-slate-800 ${sort.key === k ? "text-slate-900" : ""}`}
    >
        {label}
        {sort.key === k ? (sort.dir === "asc" ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />) : <ArrowUpDown className="h-3 w-3 opacity-40" />}
    </button>
);

export const FlatTable = ({ rows, users, depts, managerName, navigate, canEditGender, onSaved }) => {
    const [sort, setSort] = useState({ key: "name", dir: "asc" });
    const sorted = useMemo(() => {
        const dn = (u) => depts.find((d) => d.id === u.department_id)?.name || "Unassigned";
        const val = {
            name: (u) => u.name || "",
            department: (u) => u._dept,
            manager: (u) => (u.manager_id ? managerName(u.manager_id) : ""),
            role: (u) => getRoleDisplay(u, users).label || "",
            designation: (u) => u.designation || "",
            gender: (u) => u.gender || "",
            doj: (u) => (u.date_of_joining || "").slice(0, 10),
        }[sort.key];
        const all = rows.map((u) => ({ ...u, _dept: dn(u) }));
        const filled = all.filter((u) => val(u)).sort((a, b) => val(a).localeCompare(val(b)) || (a.name || "").localeCompare(b.name || ""));
        const empty = all.filter((u) => !val(u)).sort((a, b) => (a.name || "").localeCompare(b.name || ""));
        return [...(sort.dir === "asc" ? filled : filled.reverse()), ...empty];
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rows, sort, depts, users]);
    const p = { sort, setSort };
    return (
        <div className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-x-auto" data-testid="employees-flat-table">
            <div className="px-5 py-2 text-xs text-slate-500 border-b border-slate-100" data-testid="employees-flat-count">{sorted.length} people</div>
            <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-100">
                    <tr className="text-left text-xs font-semibold text-slate-500">
                        <th className="px-5 py-2.5"><span className="inline-flex items-center gap-3"><SortBtn k="name" label="Name" {...p} /><span className="text-slate-300">/</span><SortBtn k="department" label="Department" {...p} /></span></th>
                        <th className="px-5 py-2.5"><SortBtn k="gender" label="Gender" {...p} /></th>
                        <th className="px-5 py-2.5"><SortBtn k="designation" label="Designation" {...p} /></th>
                        <th className="px-5 py-2.5"><SortBtn k="doj" label="Joined" {...p} /></th>
                        <th className="px-5 py-2.5 hidden lg:table-cell"><SortBtn k="manager" label="Manager" {...p} /></th>
                        <th className="px-5 py-2.5"><SortBtn k="role" label="Role" {...p} /></th>
                        <th className="px-5 py-2.5"></th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                    {sorted.map((u) => <EmployeeRow key={u.user_id} u={u} users={users} managerName={managerName} navigate={navigate} deptName={u._dept} flat canEditGender={canEditGender} onSaved={onSaved} />)}
                </tbody>
            </table>
        </div>
    );
};
